import React, { useEffect } from 'react';
import BaseDropdown from '../form/BaseDropdown.jsx';
import BaseRadio from '../form/BaseRadio.jsx';
import BaseInputCalendar from '../form/BaseInputCalendar.jsx';
import BaseSwitch from '../form/BaseSwitch.jsx';
import { MultiSelect } from 'primereact/multiselect';
import IconCancel from '../images/IconCancel.jsx';
import dayjs from 'dayjs';

const days = {
    MO: 'monday',
    TU: 'tuesday',
    WE: 'wednesday',
    TH: 'thursday',
    FR: 'friday',
    SA: 'saturday',
    SU: 'sunday'
};

const RecurringMeeting = (props) => {
    const { setValue, watch, control } = props;
    const options = [
        { label: 'daily', value: 'daily' },
        { label: 'weekly', value: 'weekly' },
        { label: 'monthly', value: 'monthly' }
    ];

    const handleDaySelection = (day) => {
        const fullDay = days[day];

        setValue(
            'week_days',
            weekDays.includes(fullDay) ? weekDays.filter((d) => d !== fullDay) : [...weekDays, fullDay]
        );
    };

    const handleMonthDaySelection = (day, del = false) => {
        setValue('month_days', !del ? day : [dayjs(start_date).date()]);
    };

    const recurrence = watch('is_recurrent');
    const frequency = watch('frequency');
    const weekDays = watch('week_days') || [];
    const monthDays = watch('month_days') || [];
    const recurrenceEndType = watch('recurrence_end_type');

    const start_date = watch('start_date');
    const end_date = watch('end_date');

    const sameDayEvent =
        start_date && end_date && dayjs(start_date).startOf('day').isSame(dayjs(end_date).startOf('day'));

    useEffect(() => {
        if (recurrence) {
            if (!sameDayEvent) {
                setValue('is_recurrent', false);
                setValue('frequency', 'daily');
                setValue('week_days', []);
                setValue('month_days', []);
                setValue('recurrence_end_type', 'never');
            } else {
                if (frequency === 'weekly' && weekDays.length === 0) {
                    setValue('week_days', [days[dayjs(start_date).format('dd').toUpperCase()]]);
                }

                if (frequency === 'monthly' && monthDays.length === 0) {
                    setValue('month_days', [dayjs(start_date).date()]);
                }
            }
        }
    }, [frequency, monthDays.length, recurrence, sameDayEvent, setValue, start_date, weekDays.length]);

    return (
        <div className='flex flex-col justify-center items-center mb-8 px-6 py-5 bg-white rounded-4xl max-w-3xl mx-auto'>
            <div className='w-full text-theme1-text5'>
                <div className={'flex w-full justify-between items-center '}>
                    <div className='text-theme1-primary2 font-bold text-lg'>Meeting recurrence</div>
                    <BaseSwitch
                        className='w-auto'
                        control={control}
                        name='is_recurrent'
                        labelClassName='cursor-pointer'
                        disabled={!sameDayEvent}
                    />
                </div>
                {!sameDayEvent && <div>Recurring meetings are not available for multiple day events</div>}
                {recurrence && (
                    <div className={'mt-5'}>
                        <div className='flex items-center mb-6 justify-between'>
                            <span className='text-theme1-primary2 mr-3'>Repeat</span>
                            <BaseDropdown
                                control={control}
                                name={'frequency'}
                                options={options}
                                placeholder='Select a type'
                                className='w-[34%] ml-3'
                                inputClassName='bg-theme1-pinkGray rounded-lg'
                                filter={false}
                            />
                        </div>
                        {frequency === 'weekly' && (
                            <div className='mb-6'>
                                <div className='text-theme1-primary2 mb-3'>Repeat on</div>
                                <div className='flex gap-2'>
                                    {Object.keys(days).map((day, index) => (
                                        <button
                                            key={index}
                                            type='button'
                                            className={`w-10 h-10 rounded-full ${
                                                weekDays.includes(days[day])
                                                    ? 'bg-theme1-primary text-theme1-white'
                                                    : 'border-2 border-theme1-text3 bg-theme1-pinkGray text-theme1-primary text-sm font-semibold'
                                            }`}
                                            onClick={() => handleDaySelection(day)}
                                        >
                                            {day}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {frequency === 'monthly' && (
                            <div className='mb-6'>
                                <div className='flex items-center justify-between'>
                                    <span className='text-theme1-primary2'>Monthly on</span>
                                    <div className='flex items-center gap-2'>
                                        <MultiSelect
                                            value={monthDays}
                                            onChange={(e) => handleMonthDaySelection(e.value)}
                                            options={Array.from({ length: 31 }, (_, i) => ({
                                                label: i + 1,
                                                value: i + 1
                                            }))}
                                            optionLabel='label'
                                            filter={false}
                                            showSelectAll={false}
                                            placeholder={'Month date'}
                                            maxSelectedLabels={5}
                                            className={`bg-transparent border-theme1-text3 border rounded-4xl px-2 ${monthDays.length > 0 ? 'w-[90%]' : 'w-full'}`}
                                        />
                                        {monthDays.length > 0 && (
                                            <div
                                                className='cursor-pointer w-[36px] flex-shrink-0'
                                                onClick={() => handleMonthDaySelection([], true)}
                                            >
                                                <IconCancel className='fill-theme1-primary' />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        <div>
                            <div className='flex flex-col gap-4'>
                                <span className='text-theme1-primary2'>Ends</span>
                                <div className='flex items-center gap-2'>
                                    <BaseRadio
                                        control={control}
                                        name='recurrence_end_type'
                                        value='never'
                                        textValue='Never'
                                        className={'flex text-theme1-primary2'}
                                        childrenClassName={'ml-3'}
                                    />
                                </div>
                                <div className='w-full flex items-center justify-between'>
                                    <BaseRadio
                                        control={control}
                                        name='recurrence_end_type'
                                        value='on'
                                        textValue='On'
                                        className={'flex text-theme1-primary2'}
                                        childrenClassName={'ml-3'}
                                    />
                                    <BaseInputCalendar
                                        control={control}
                                        name='recurrence_end_date'
                                        placeholder='date'
                                        className={'custom-input ml-5 w-[190px]'}
                                        inputClassName={`rounded-lg ${recurrenceEndType && recurrenceEndType === 'on' ? 'bg-theme1-pinkGray text-theme1-text3' : 'bg-theme1-disabled text-theme1-text2'}`}
                                        disabled={recurrenceEndType && recurrenceEndType !== 'on'}
                                        inputStyle={{
                                            background:
                                                recurrenceEndType && recurrenceEndType === 'on' ? '#F8F1F1' : '#BEA9A9'
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

RecurringMeeting.displayName = 'RecurringMeeting';

export default RecurringMeeting;
